import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from './telemetry/posthog/frontend';

/**
 * Subset of the experimental SpeechRecognition API surface we touch. Spelled
 * inline so this hook isn't dependent on `dom.iterable` lib types or the
 * `webspeechapi` types package.
 */
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<{
    isFinal: boolean;
    [index: number]: { transcript: string };
  }>;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechWindow {
  SpeechRecognition?: SpeechRecognitionCtor;
  webkitSpeechRecognition?: SpeechRecognitionCtor;
}

/** Resolve a SpeechRecognition constructor on browsers that ship one. */
function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as SpeechWindow;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** What {@link useSpeechDictation} returns. */
export interface SpeechDictation {
  /** True when the browser ships a SpeechRecognition implementation. */
  supported: boolean;
  /** True while the mic is actively listening. */
  recording: boolean;
  /** Toggle listening on / off. */
  toggle: () => void;
  /** Stop listening (a no-op if not recording). */
  stop: () => void;
}

/**
 * Browser dictation via the Web Speech API, appending the transcript to a
 * controlled text value. Unsupported browsers report `supported: false` so the
 * caller can hide the mic rather than expose a dead control (Firefox/Safari).
 *
 * It keeps listening across natural pauses (`continuous`) until the user stops,
 * snapshots the value when recording starts so the transcript appends to what
 * was already typed, surfaces interim words as they are spoken, and aborts on
 * unmount so the mic does not keep streaming after navigation.
 *
 * @param value Current text value (snapshotted when recording starts).
 * @param onChange Receives the merged value as the transcript streams in.
 * @param context `Component.method` tag for error telemetry.
 * @param disabled When true, starting is a no-op.
 */
export function useSpeechDictation(
  value: string,
  onChange: (value: string) => void,
  context: string,
  disabled = false,
): SpeechDictation {
  const ctor = useMemo(getSpeechRecognitionCtor, []);
  const supported = ctor !== null;
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [recording, setRecording] = useState(false);

  // Cancel in-flight speech on unmount so the mic doesn't keep streaming after
  // the surface is torn down.
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.abort();
      } catch {
        /* ignore - already torn down */
      }
    };
  }, []);

  const start = useCallback(() => {
    if (!ctor || disabled) return;
    try {
      const recognition = new ctor();
      recognition.lang = navigator.language || 'en-US';
      recognition.interimResults = true;
      // `continuous: true` keeps the recognizer running across natural pauses.
      // With `false` the browser auto-stops the moment it detects ~a second of
      // silence, which feels like the mic timing out a few seconds in.
      recognition.continuous = true;

      // Snapshot the value at recording start so interim transcripts append to
      // whatever the user already had typed.
      const baseValue = value;
      let finalised = '';

      recognition.onresult = (event) => {
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0]?.transcript ?? '';
          if (result.isFinal) {
            finalised += transcript;
          } else {
            interim += transcript;
          }
        }
        const merged = (baseValue ? `${baseValue} ` : '') +
          (finalised + interim).trimStart();
        onChange(merged.trimStart());
      };
      recognition.onerror = (e) => {
        if (e.error !== 'aborted' && e.error !== 'no-speech') {
          captureException(
            new Error(`SpeechRecognition error: ${e.error ?? 'unknown'}`),
            context,
            { error: e.error },
          );
        }
        setRecording(false);
      };
      recognition.onend = () => setRecording(false);

      recognition.start();
      recognitionRef.current = recognition;
      setRecording(true);
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), context, {});
      setRecording(false);
    }
  }, [ctor, disabled, value, onChange, context]);

  const stop = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* ignore - already stopped */
    }
    setRecording(false);
  }, []);

  const toggle = useCallback(() => {
    if (recording) stop();
    else start();
  }, [recording, start, stop]);

  return { supported, recording, toggle, stop };
}
